"use client";

import React from "react";

type NavbarSettingsContextType = {
  /**
   * The element whose top edge turns the navbar opaque. `null` on routes that
   * have not opted in, which keeps the navbar in its default opaque state.
   */
  opaqueTrigger: HTMLElement | null;
  setOpaqueTrigger: (element: HTMLElement | null) => void;
};

type NavbarSettingsProviderProps = React.PropsWithChildren;

const NavbarSettingsContext =
  React.createContext<NavbarSettingsContextType | null>(null);

/**
 * Lets a route hand the navbar the element it should watch. The navbar renders
 * above the page in the layout, so the element has to travel up through context.
 */
const NavbarSettingsProvider = ({ children }: NavbarSettingsProviderProps) => {
  const [opaqueTrigger, setOpaqueTrigger] = React.useState<HTMLElement | null>(
    null,
  );

  const value = React.useMemo(
    () => ({ opaqueTrigger, setOpaqueTrigger }),
    [opaqueTrigger],
  );

  return (
    <NavbarSettingsContext.Provider value={value}>
      {children}
    </NavbarSettingsContext.Provider>
  );
};

/**
 * Throws outside the provider rather than degrading silently: a route that opts
 * in and gets no navbar response is a bug worth surfacing.
 */
const useNavbarSettings = (): NavbarSettingsContextType => {
  const context = React.useContext(NavbarSettingsContext);

  if (!context) {
    throw new Error(
      "useNavbarSettings must be used within a NavbarSettingsProvider",
    );
  }

  return context;
};

export { NavbarSettingsContext, NavbarSettingsProvider, useNavbarSettings };

export type { NavbarSettingsContextType };
